import React, { Component } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import API from "../utils/api";
import { loadCallStatusLabels, getCallStatusLabel } from "../utils/callStatusCache";
import { sanitizePhone } from "../utils/phone";

// Format backend validation errors:
// "medicine_type" must contain at least 1 items {middlewares/enquiryV2/validateLead}
// → Medicine Type must contain at least 1 items
const formatApiError = (msg) => {
    if (typeof msg !== "string") return msg;
    // Remove {middlewares/...} suffix
    msg = msg.replace(/\s*\{[^}]*\}\s*$/, "").trim();
    // Convert "snake_case_field" to Title Case
    msg = msg.replace(/"([a-z_]+)"/g, (_, field) =>
        field.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
    );
    return msg;
};

// Only these are hardcoded (not returned by /const-data)
const GST_OPTIONS = [
    { value: "", label: "Select" },
    { value: "1", label: "Yes" },
    { value: "0", label: "No" },
    { value: "2", label: "Applied" },
];

const DRUG_LICENSE_OPTIONS = [
    { value: "", label: "Select" },
    { value: "1", label: "Yes" },
    { value: "0", label: "No" },
    { value: "2", label: "Applied" },
];

const FSSAI_LICENSE_OPTIONS = [
    { value: "", label: "Select" },
    { value: "1", label: "Yes" },
    { value: "0", label: "No" },
    { value: "2", label: "Applied" },
];

// Helper: convert array of strings to { value, label } options
const toOptions = (arr) =>
    (arr || []).map((item) => ({ value: item, label: item }));

// Determine duplicate check button class based on recency of most recent duplicate
const getDupBtnClass = (duplicates, checked) => {
    if (!checked) return "btn btn-warning";
    if (!duplicates || duplicates.length === 0) return "btn btn-success";

    // Find most recent posted_on_raw
    let mostRecent = null;
    for (const l of duplicates) {
        const raw = l.posted_on_raw || l.posted_on;
        if (raw) {
            const d = new Date(raw);
            if (!isNaN(d.getTime()) && (!mostRecent || d > mostRecent)) mostRecent = d;
        }
    }

    if (!mostRecent) return "btn btn-danger";

    const diffDays = (Date.now() - mostRecent.getTime()) / 86400000;

    if (diffDays <= 3) return "btn btn-danger";   // red — very recent
    if (diffDays <= 7) return "btn btn-warning";  // orange — within a week
    if (diffDays <= 15) return "btn btn-info";    // light blue — within 15 days
    if (diffDays <= 30) return "btn btn-primary"; // blue — within a month
    return "btn btn-success";                     // green — older than a month
};

// Call status to lead status mapping
// Map call_status to lead status (0 or 9)
// 0 = Pending, 1 = Phone Not Picked, 2 = Phone Switched off, 3 = Phone Not Reachable
// 5 = No Requirement, 6 = Callback Later, 8 = No DL, 9 = Duplicate, 11 = Spam, 13 = Already Done, 14 = Done, 15 = Personal Use
const CALL_STATUS_TO_LEAD_STATUS = {
    0: null,   // Pending -> no change
    1: null,   // Phone Not Picked -> no change
    2: null,   // Phone Switched off -> no change
    3: null,   // Phone Not Reachable -> no change
    5: 0,      // No Requirement -> set status to 0
    6: null,   // Callback Later -> no change
    8: 0,      // No DL -> set status to 0
    9: 0,      // Duplicate -> set status to 0
    11: 0,     // Spam -> set status to 0
    13: 0,     // Already Done -> set status to 0
    14: null,  // Done -> special handling (auto-check verified)
    15: 0,     // Personal Use -> set status to 0
};

// Helper function to get background color for call status dropdown
const getCallStatusOptionColor = (callStatusNum) => {
    const statusNum = Number(callStatusNum);
    const mapping = CALL_STATUS_TO_LEAD_STATUS[statusNum];

    if (statusNum === 14) return "#d4edda"; // green for Done
    if (mapping === 0) return "#f8d7da"; // red for statuses mapped to 0
    return "#cfe2ff"; // blue for others
};

class LeadForm extends Component {
    state = {
        // Form fields
        email: "",
        name: "",
        mobile: "",
        whatsapp: "",
        address: null, // react-select object
        medicine_type: [],
        service_type: "",
        service_for: [],
        primary_service_for: "",
        pcd_area: null,
        quantity: "",
        order_value: "",
        call_time: "",
        required_when: "",
        source: "",
        selling_purpose: "",
        investment: "",
        gst_number: "",
        drug_license: "",
        fssai_license: "",
        experience: "",
        profession: "",
        firm_name: "",
        message: "",
        status: false, // checkbox for verified (status=9)
        call_status: "", // tele-caller call status
        remark: "", // tele-caller remark

        // Call status options (loaded from API)
        callStatusOptions: [],
        callStatusLabels: {},

        // UI state
        submitting: false,
        phoneChecked: false,
        emailChecked: false,
        phoneDuplicates: null,
        emailDuplicates: null,
        mobileError: "",
        whatsappError: "",
        serviceForOptions: [],
        medicineTypeOptions: [],
        serviceTypeOptions: [],
        callTimeOptions: [],
        requiredWhenOptions: [],
        sourceOptions: [],
        campaignOptions: [],
        campaign: "",
        teleSource: [],
        sellingPurposeOptions: [],
        experienceOptions: [],
        professionOptions: [],
        loadingConstData: true,
        // Result modal
        resultModal: null, // { type: 'success' | 'error', message: string, leadId?: number }
        showPreviewModal: false, // For Lead Preview
        previewConfirmed: false, // For Lead Preview Checkbox
    };

    componentDidMount() {
        this.loadConstData();
        this.loadCallStatusLabels();

        // If editing, populate form
        if (this.props.lead) {
            this.populateFromLead(this.props.lead);
        }

        // If prefillMobile is provided (from phone-first Add Lead flow), set mobile
        if (!this.props.isEdit && this.props.prefillMobile) {
            const sanitized = sanitizePhone(this.props.prefillMobile);
            if (sanitized) {
                this.setState({ mobile: sanitized });
            }
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.lead && this.props.lead) {
            this.populateFromLead(this.props.lead);
        }
    }

    populateFromLead = (lead) => {
        const rawMedicine = lead.medicine_type || "";
        const medicineArr = Array.isArray(rawMedicine) ? rawMedicine : rawMedicine.split(",");
        const medicineTypes = medicineArr.filter(Boolean).map((v) => ({ value: v.trim(), label: v.trim() }));

        const rawService = lead.service_for || "";
        const serviceArr = Array.isArray(rawService) ? rawService : rawService.split(",");
        const serviceFor = serviceArr.filter(Boolean).map((v) => ({ value: v.trim(), label: v.trim() }));

        this.setState({
            email: lead.email || "",
            name: lead.name || "",
            mobile: lead.mobile || "",
            whatsapp: lead.whatsapp || "",
            address: lead.address ? { value: lead.address, label: lead.address } : null,
            medicine_type: medicineTypes,
            service_type: lead.service_type || "",
            service_for: serviceFor,
            primary_service_for: lead.primary_service_for || "",
            pcd_area: lead.pcd_area ? { value: lead.pcd_area, label: lead.pcd_area } : null,
            quantity: lead.quantity || "",
            order_value: lead.order_value || "",
            call_time: lead.call_time || "",
            required_when: lead.required_when || "",
            source: lead.source || "",
            campaign: lead.campaign_name || lead.campaign || "",
            selling_purpose: lead.selling_purpose || "",
            investment: lead.investment || "",
            gst_number: lead.gst_number !== undefined ? String(lead.gst_number) : "",
            drug_license: lead.drug_license !== undefined ? String(lead.drug_license) : "",
            fssai_license: lead.fssai_license !== undefined ? String(lead.fssai_license) : "",
            experience: lead.experience || "",
            profession: lead.profession || "",
            firm_name: lead.firm_name || "",
            message: lead.message || "",
            status: lead.status === 9 || lead.status === "9",
            // If status is verified (9), auto-set call_status to 14 (Done)
            call_status: (lead.status === 9 || lead.status === "9") ? "14" : (lead.call_status || ""),
            remark: lead.remark || "",
            phoneChecked: false,
            emailChecked: !lead.email, // no check needed if email is empty
        });
    };

    loadConstData = async () => {
        try {
            const res = await API.get("/const-data");
            const data = res.data.data || res.data;
            this.setState({
                serviceForOptions: toOptions(data.service_for),
                medicineTypeOptions: toOptions(data.medicine_type),
                serviceTypeOptions: toOptions(data.enquiry_type),
                callTimeOptions: toOptions(data.call_time),
                requiredWhenOptions: toOptions(data.required_when),
                sourceOptions: [...new Set((data.tele_source || []).map(s => s.source))].map(source => ({ value: source, label: source })),
                teleSource: data.tele_source || [],
                sellingPurposeOptions: toOptions(data.selling_purpose),
                experienceOptions: toOptions(data.experience),
                professionOptions: toOptions(data.profession),
                loadingConstData: false,
            });
        } catch (e) {
            console.error("Failed to load const-data", e);
            this.setState({ loadingConstData: false });
        }
    };

    loadCallStatusLabels = async () => {
        try {
            const { callStatusLabels, callStatuses } = await loadCallStatusLabels();
            this.setState({
                callStatusOptions: (callStatuses || []).map((s) => ({
                    value: String(s),
                    label: callStatusLabels[s] || String(s),
                })),
                callStatusLabels,
            });
        } catch (e) {
            console.error("Failed to load call status labels", e);
        }
    };

    loadCities = async (inputValue) => {
        if (!inputValue || inputValue.length < 2) return [];
        try {
            const res = await API.get("/cities", { params: { q: inputValue } });
            const cities = res.data.data || res.data;
            return cities.map((c) => ({
                value: `${c.city}, ${c.state}`,
                label: `${c.city}, ${c.state}`,
                id: c.id,
            }));
        } catch (e) {
            return [];
        }
    };

    handleCheckDuplicate = async (type) => {
        const value = type === "phone" ? this.state.mobile : this.state.email;
        if (!value) {
            toast.warn(`Please enter ${type} first`);
            return;
        }
        if (type === "phone" && value.length !== 10) {
            toast.warn("Phone must be 10 digits");
            return;
        }

        try {
            const res = await API.get("/check-duplicate", { params: { type, value } });
            const data = res.data.data || res.data;
            const leads = data.leads || data || [];

            if (type === "phone") {
                const updates = {
                    phoneDuplicates: leads,
                    phoneChecked: true,
                };

                // Auto-fill empty customer fields from first duplicate
                if (leads.length > 0) {
                    const first = leads[0];
                    const fillMap = {
                        name: "name",
                        email: "email",
                        experience: "experience",
                        firm_name: "firm_name",
                        gst_number: "gst_number",
                        drug_license: "drug_license",
                        profession: "profession",
                    };
                    for (const [apiField, stateField] of Object.entries(fillMap)) {
                        if (!this.state[stateField] && first[apiField]) {
                            updates[stateField] = stateField === "gst_number" || stateField === "drug_license"
                                ? String(first[apiField])
                                : first[apiField];
                        }
                    }
                    // If email was auto-filled, mark email duplicate check as pending
                    if (updates.email && updates.email !== this.state.email) {
                        updates.emailChecked = false;
                    }
                }

                this.setState(updates);
                if (leads.length > 0) {
                    toast.warn(`Found ${leads.length} existing lead(s) with this phone`);
                } else {
                    toast.success("No duplicates found");
                }
            } else {
                this.setState({ emailDuplicates: leads, emailChecked: true });
                if (leads.length > 0) {
                    toast.warn(`Found ${leads.length} existing lead(s) with this email`);
                } else {
                    toast.success("No duplicates found");
                }
            }
        } catch (e) {
            toast.error("Duplicate check failed");
        }
    };

    handleCall = async () => {
        if (!this.state.mobile) {
            toast.warn("Please enter phone number to call");
            return;
        }
        if (this.state.mobile.length !== 10) {
            toast.warn("Phone must be 10 digits");
            return;
        }

        try {
            await API.post("/do-call", { phoneNumber: this.state.mobile });
            toast.success("Call initiated on your mobile device");
        } catch (e) {
            let msg = "Failed to initiate call";
            if (e.response && e.response.data) {
                const d = e.response.data;
                msg = d.message || d.error || d.msg || msg;
            } else if (e.message) {
                msg = e.message;
            }
            toast.error(msg);
        }
    };

    handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === "checkbox") {
            if (name === "status") {
                // When verified checkbox is toggled
                const updates = { [name]: checked };

                if (checked) {
                    // If verified is checked, auto-set call_status to "14" (Done)
                    updates.call_status = "14";
                } else {
                    // If verified is unchecked, clear call_status
                    updates.call_status = "";
                }

                this.setState(updates);
            } else {
                this.setState({ [name]: checked });
            }
        } else if (name === "call_status") {
            // When call_status changes, apply status mapping
            const callStatusNum = Number(value);
            const statusMapping = CALL_STATUS_TO_LEAD_STATUS[callStatusNum];

            const updates = { [name]: value };

            if (callStatusNum === 14) {
                // Special case: call_status = 14 (Done) should auto-check verified
                updates.status = true;
            } else {
                // Any status other than Done should uncheck verified
                updates.status = false;
            }

            this.setState(updates);
        } else if (name === "mobile" || name === "whatsapp") {
            const sanitized = sanitizePhone(value);
            const errorKey = name === "mobile" ? "mobileError" : "whatsappError";
            if (sanitized.length > 10) {
                this.setState({
                    [name]: sanitized,
                    [errorKey]: `Number has ${sanitized.length} digits — must be exactly 10`,
                });
            } else {
                this.setState({ [name]: sanitized, [errorKey]: "" });
            }
            if (name === "mobile") {
                this.setState({ phoneChecked: false, phoneDuplicates: null });
            }
        } else if (name === "source") {
            const campaignOptions = this.state.teleSource
                .filter(c => c.source === value)
                .map(c => ({ value: c.campaign_name, label: c.campaign_name }));

            const defaultCampaign = this.state.teleSource.find(c => c.source === value && c.is_default === 1);
            const campaign = defaultCampaign ? defaultCampaign.campaign_name : (campaignOptions.length > 0 ? campaignOptions[0].value : "");

            this.setState({
                source: value,
                campaignOptions,
                campaign,
            });
        } else {
            this.setState({ [name]: value });
            // Reset duplicate checks when email changes
            if (name === "email") {
                this.setState({ emailChecked: false, emailDuplicates: null });
            }
        }
    };

    handleSubmit = async (e, confirmed = false) => {
        if (e) e.preventDefault();

        // Validation — collect all errors and show in popup
        const validationErrors = [];
        if (this.state.status && this.state.call_status == 14) {
            if (!this.state.address) validationErrors.push("Address is required");
            if (!this.state.phoneChecked) validationErrors.push("Please check phone for duplicates first");
            if (this.state.email && !this.state.emailChecked) validationErrors.push("Please check email for duplicates first");
            if (!this.state.service_type) validationErrors.push("Service Type is required");
            if (!this.state.profession) validationErrors.push("Profession is required");
            if (this.state.medicine_type.length === 0) validationErrors.push("Please select at least one medicine type");
            if (!this.state.primary_service_for) validationErrors.push("Primary Service Category is required");
            if (!this.state.source) validationErrors.push("Lead Source is required");
            if (!this.state.message || this.state.message.trim().length < 10) validationErrors.push("Message must be at least 10 characters long");
            if (!this.state.call_time) validationErrors.push("Call Time is required");
            if (this.state.medicine_type.some((m) => m.value === "Nutraceutical") && !this.state.fssai_license) {
                validationErrors.push("FSSAI Licence is required when Nutraceutical is selected");
            }

            if (this.state.service_type === "PCD Pharma Franchise") {
                if (!this.state.pcd_area) validationErrors.push("PCD Area is required for PCD Pharma Franchise service type");
            } else {
                if (!this.state.quantity) validationErrors.push("Quantity is required for Third party service types");
            }
        }

        // When editing, call_status is required
        if (this.props.isEdit && !this.state.call_status && this.state.call_status !== 0) {
            validationErrors.push("Call Status is required when updating a lead");
        }

        if (validationErrors.length > 0) {
            this.setState({
                resultModal: {
                    type: "error",
                    message: validationErrors.join("\n"),
                },
            });
            return;
        }

        if (!confirmed && (this.state.status || this.state.call_status == 14)) {
            this.setState({ showPreviewModal: true, previewConfirmed: false });
            return;
        }

        this.setState({ submitting: true });

        let statusToSend = this.state.status ? 9 : (this.props.lead?.status || 5);

        let body = {
            email: this.state.email,
            name: this.state.name ? this.state.name.trim() : undefined,
            mobile: this.state.mobile,
            whatsapp: this.state.whatsapp,
            address: this.state.address ? this.state.address.value : undefined,
            medicine_type: this.state.medicine_type.map((m) => m.value),
            service_type: this.state.service_type ? this.state.serviceTypeOptions.find((o) => o.value === this.state.service_type)?.value : undefined,
            service_for: this.state.service_for.length ? this.state.service_for.map((s) => s.value) : [this.state.primary_service_for],
            primary_service_for: this.state.primary_service_for,
            pcd_area: this.state.pcd_area ? this.state.pcd_area.value : undefined,
            quantity: this.state.quantity,
            order_value: this.state.order_value,
            call_time: this.state.call_time ? this.state.callTimeOptions.find((o) => o.value === this.state.call_time)?.value : undefined,
            required_when: this.state.required_when ? this.state.requiredWhenOptions.find((o) => o.value === this.state.required_when)?.value : undefined,
            source: this.state.source,
            campaign: this.state.campaign,
            selling_purpose: this.state.selling_purpose ? this.state.sellingPurposeOptions.find((o) => o.value === this.state.selling_purpose)?.value : undefined,
            investment: this.state.investment,
            gst_number: this.state.gst_number,
            drug_license: this.state.drug_license,
            fssai_license: this.state.fssai_license,
            experience: this.state.experience ? this.state.experienceOptions.find((o) => o.value === this.state.experience)?.value : undefined,
            profession: this.state.profession ? this.state.professionOptions.find((o) => o.value === this.state.profession)?.value : undefined,
            firm_name: this.state.firm_name,
            message: this.state.message ? this.state.message.trim() : undefined,
            status: statusToSend,
        };

        // Add call_status and remark
        if (this.state.call_status !== "" && this.state.call_status !== null) {
            body.call_status = Number(this.state.call_status);

            // If call_status is 14 (Done), ensure status is set to 9 (Verified)
            if (body.call_status == 14) {
                statusToSend = 9;
            } else if ([5, 8, 9, 11, 13, 15].includes(Number(body.call_status))) {
                // For other call_status values, set status to 0 (Pending)
                statusToSend = 0;

                // Keeping here for reference
                // CALL_STATUS: [
                //     0, // -> Pending, -> No change in lead status
                //     1, // -> Phone Not Picked, -> No change in lead status
                //     2, // -> Phone Switched off, -> No change in lead status
                //     3, // -> Phone Not Reachable, -> No change in lead status
                //     // 4, // -> No Response, -> No change in lead status
                //     5, // -> No Requirement, -> mapped with 0 lead status
                //     6, // -> Callback Later, -> No change in lead status
                //     // 7, // -> Dead, -> mapped with 0 lead status
                //     8, // -> No DL, -> No change in lead status
                //     9, // -> Duplicate, -> mapped with 0 lead status
                //     // 10, // -> Follow up, -> No change in lead status
                //     11, // -> Spam -> mapped with 0 lead status
                //     // 12, // -> Blacklist -> mapped with 0 lead status
                //     13, // -> Already Done, -> mapped with 0 lead status
                //     14, // -> Done -> mapped with 9 lead status
                //     15, // -> Personal Use -> mapped with 0 lead status
                // ],
                // LEAD_STATUS_MAPPING: {
                //     14: 9,
                //     5: 0,
                //     8: 0, // As per feedback from tele team, no DL leads are marked as 0
                //     9: 0,
                //     11: 0,
                //     13: 0,
                //     15: 0,
                // }
            }
        }
        body.status = statusToSend;

        if (this.state.remark) {
            body.remark = this.state.remark;
        }

        // check if medicine_type and service_for are empty arrays or contain empty string, if so delete them from body to prevent validation error
        if (body.medicine_type.length) {
            body.medicine_type = body.medicine_type.filter(m => m); // remove empty strings
        }
        if (body.service_for.length) {
            body.service_for = body.service_for.filter(s => s); // remove empty strings
        }


        if (body.email === "" || body.email === null || body.email == "null") delete body.email;
        if (body.whatsapp === "" || body.whatsapp === null || body.whatsapp == "null") delete body.whatsapp;
        if (body.quantity === "" || body.quantity === null || body.quantity == "null") delete body.quantity;
        if (body.order_value === "" || body.order_value === null || body.order_value == "null") delete body.order_value;
        if (body.call_time === "" || body.call_time === null || body.call_time == "null") delete body.call_time;
        if (body.required_when === "" || body.required_when === null || body.required_when == "null") delete body.required_when;
        if (body.source === "" || body.source === null || body.source == "null") delete body.source;
        if (body.selling_purpose === "" || body.selling_purpose === null || body.selling_purpose == "null") delete body.selling_purpose;
        if (body.investment === "" || body.investment === null || body.investment == "null") delete body.investment;
        if (body.gst_number === "" || body.gst_number === null || body.gst_number == "null") delete body.gst_number;
        if (body.drug_license === "" || body.drug_license === null || body.drug_license == "null") delete body.drug_license;
        if (body.fssai_license === "" || body.fssai_license === null || body.fssai_license == "null") delete body.fssai_license;
        if (body.experience === "" || body.experience === null || body.experience == "null") delete body.experience;
        if (body.profession === "" || body.profession === null || body.profession == "null") delete body.profession;
        if (body.firm_name === "" || body.firm_name === null || body.firm_name == "null") delete body.firm_name;
        if (body.primary_service_for === "" || body.primary_service_for === null || body.primary_service_for == "null") delete body.primary_service_for;
        if (body.pcd_area === "" || body.pcd_area === null || body.pcd_area == "null") delete body.pcd_area;
        if (body.medicine_type.length === 0) delete body.medicine_type;
        if (body.service_for.length === 0) delete body.service_for;
        if (body.primary_service_for === "" || body.primary_service_for === null || body.primary_service_for == "null") delete body.primary_service_for;



        try {
            if (this.props.isEdit) {
                const res = await API.post(`/lead/update/${this.props.leadId}`, body, { timeout: 60000 });
                const resData = res.data.data || res.data;
                const finalStatus = resData.posted;
                this.setState({
                    submitting: false,
                    resultModal: {
                        type: "success",
                        message: "Lead updated successfully",
                        leadId: this.props.leadId,
                        posted: finalStatus,
                    },
                });
            } else {
                const res = await API.post("/lead", body, { timeout: 60000 });
                const resData = res.data.data || res.data;
                const leadId = resData.enquiry_id;
                const finalStatus = resData.posted;
                this.setState({
                    submitting: false,
                    resultModal: {
                        type: "success",
                        message: "Lead created successfully",
                        leadId,
                        posted: finalStatus,
                    },
                });
            }
        } catch (e) {
            let msg = "Failed to save lead";
            if (e.response && e.response.data) {
                const d = e.response.data;
                msg = d.message || d.error || d.msg || msg;
            } else if (e.message) {
                msg = e.message;
            }
            this.setState({
                submitting: false,
                resultModal: {
                    type: "error",
                    message: formatApiError(msg),
                },
            });
        }
    };

    render() {
        const {
            email, name, mobile, whatsapp, address,
            medicine_type, service_type, service_for, primary_service_for, pcd_area, quantity,
            order_value, call_time, required_when, source, selling_purpose,
            investment, gst_number, drug_license, fssai_license, experience, profession,
            firm_name, message, status, submitting, phoneChecked,
            phoneDuplicates, emailDuplicates, emailChecked, mobileError,
            whatsappError, serviceForOptions,
            medicineTypeOptions, serviceTypeOptions, callTimeOptions,
            requiredWhenOptions, sourceOptions, sellingPurposeOptions,
            experienceOptions, professionOptions, loadingConstData,
            showPreviewModal,
            previewConfirmed
        } = this.state;

        const { resultModal } = this.state;
        const isPCD = service_type === "PCD Pharma Franchise";
        const is3rd = service_type === "3rd Party Manufacturing";
        const hasNutraceutical = medicine_type.some((m) => m.value === "Nutraceutical");

        // Determine pending duplicate checks
        const pendingChecks = [];
        if (!phoneChecked) pendingChecks.push("Phone");
        if (email && !emailChecked) pendingChecks.push("Email");
        const allChecked = pendingChecks.length === 0;

        return (
            <form onSubmit={this.handleSubmit} className="form-section">
                <div className="row">

                    {/* Phone with duplicate check */}
                    <div className="col-md-6">
                        <div className="form-group">
                            <label>Phone*</label>
                            <div className="input-group">
                                <input
                                    type="text"
                                    className={`form-control${mobileError ? " input-error" : ""}`}
                                    name="mobile"
                                    value={mobile}
                                    onChange={this.handleChange}
                                    placeholder="10 digit phone number"
                                />
                                <span className="input-group-btn">
                                    <button
                                        type="button"
                                        className={getDupBtnClass(phoneDuplicates, phoneChecked)}
                                        onClick={() => this.handleCheckDuplicate("phone")}
                                    >
                                        Duplicate Check
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary ml-1"
                                        onClick={this.handleCall}
                                        title="Call Customer"
                                    >
                                        <i className="fas fa-phone"></i> Call
                                    </button>
                                </span>
                            </div>
                            {mobileError && (
                                <span className="text-danger" style={{ fontSize: 12 }}>
                                    <i className="fas fa-exclamation-circle"></i> {mobileError}
                                </span>
                            )}
                            {phoneDuplicates && phoneDuplicates.length > 0 && (
                                <DuplicateResults leads={phoneDuplicates} />
                            )}
                        </div>
                    </div>


                    {/* Email with duplicate check */}
                    <div className="col-md-6">
                        <div className="form-group">
                            <label>Email</label>
                            <div className="input-group">
                                <input
                                    type="email"
                                    className="form-control"
                                    name="email"
                                    value={email}
                                    onChange={this.handleChange}
                                    maxLength={256}
                                    placeholder="Email address"
                                />
                                <span className="input-group-btn">
                                    <button
                                        type="button"
                                        className={getDupBtnClass(emailDuplicates, emailChecked)}
                                        onClick={() => this.handleCheckDuplicate("email")}
                                    >
                                        Duplicate Check
                                    </button>
                                </span>
                            </div>
                            {emailDuplicates && emailDuplicates.length > 0 && (
                                <DuplicateResults leads={emailDuplicates} />
                            )}
                        </div>
                    </div>

                </div>

                <div className="row">

                    {/* Customer Name */}
                    <div className="col-md-6">
                        <div className="form-group">
                            <label>Customer Name</label>
                            <input
                                type="text"
                                className="form-control"
                                name="name"
                                value={name}
                                onChange={this.handleChange}
                                maxLength={50}
                                placeholder="Customer Name"
                            />
                        </div>
                    </div>
                    {/* Whatsapp */}
                    <div className="col-md-6">
                        <div className="form-group">
                            <label>Whatsapp</label>
                            <input
                                type="text"
                                className={`form-control${whatsappError ? " input-error" : ""}`}
                                name="whatsapp"
                                value={whatsapp}
                                onChange={this.handleChange}
                                placeholder="WhatsApp number"
                            />
                            {whatsappError && (
                                <span className="text-danger" style={{ fontSize: 12 }}>
                                    <i className="fas fa-exclamation-circle"></i> {whatsappError}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="row">
                    {/* Address (city search) */}
                    <div className="col-md-6">
                        <div className="form-group">
                            <label>Customer Address*</label>
                            <AsyncSelect
                                cacheOptions
                                loadOptions={this.loadCities}
                                value={address}
                                onChange={(val) => this.setState({ address: val })}
                                placeholder="Search city..."
                                isClearable
                            />
                        </div>
                    </div>


                    {/* Service Type */}
                    <div className="col-md-6">
                        <div className="form-group">
                            <label>Service Type*</label>
                            <select
                                className="form-control"
                                name="service_type"
                                value={service_type}
                                onChange={this.handleChange}
                            >
                                <option value="">Select</option>
                                {serviceTypeOptions.map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="row">

                    {/* Medicine Type (multi) */}
                    <div className="col-md-6">
                        <div className="form-group">
                            <label>Medicine Type*</label>
                            <Select
                                isMulti
                                options={medicineTypeOptions}
                                value={medicine_type}
                                onChange={(val) => this.setState({ medicine_type: val || [] })}
                                placeholder="Select medicine types..."
                            />
                        </div>
                    </div>

                    {/* Drug License */}
                    <div className={hasNutraceutical ? "col-md-2" : "col-md-3"}>
                        <div className="form-group">
                            <label>Drug License Available*</label>
                            <select
                                className="form-control"
                                name="drug_license"
                                value={drug_license}
                                onChange={this.handleChange}
                            >
                                {DRUG_LICENSE_OPTIONS.map((d) => (
                                    <option key={d.value} value={d.value}>
                                        {d.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* FSSAI Licence - only when Nutraceutical is selected */}
                    {hasNutraceutical && (
                        <div className="col-md-2">
                            <div className="form-group">
                                <label>FSSAI License Available*</label>
                                <select
                                    className="form-control"
                                    name="fssai_license"
                                    value={fssai_license}
                                    onChange={this.handleChange}
                                >
                                    {FSSAI_LICENSE_OPTIONS.map((f) => (
                                        <option key={f.value} value={f.value}>
                                            {f.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {/* GST Number */}
                    <div className={hasNutraceutical ? "col-md-2" : "col-md-3"}>
                        <div className="form-group">
                            <label>GST Available*</label>
                            <select
                                className="form-control"
                                name="gst_number"
                                value={gst_number}
                                onChange={this.handleChange}
                            >
                                {GST_OPTIONS.map((g) => (
                                    <option key={g.value} value={g.value}>
                                        {g.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                </div>

                <div className="row">




                    {/* Primary Service Category (single select) */}
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>Primary Service Category *</label>
                            <select
                                className="form-control"
                                name="primary_service_for"
                                value={primary_service_for}
                                onChange={this.handleChange}
                            >
                                <option value="">Select</option>
                                {serviceForOptions.map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Service For (multi) */}
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>Secondary Service Category</label>
                            <Select
                                isMulti
                                options={serviceForOptions}
                                value={service_for}
                                onChange={(val) => this.setState({ service_for: val || [] })}
                                placeholder="Select service..."
                                isLoading={loadingConstData}
                            />
                        </div>
                    </div>



                    {/* PCD Area - only for PCD */}
                    {isPCD && (
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>PCD Area</label>
                                <AsyncSelect
                                    required
                                    cacheOptions
                                    loadOptions={this.loadCities}
                                    value={pcd_area}
                                    onChange={(val) => this.setState({ pcd_area: val })}
                                    placeholder="Search PCD area..."
                                    isClearable
                                />
                            </div>
                        </div>
                    )}

                    {/* Quantity - only for 3rd Party */}
                    {is3rd && (
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>Quantity</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="quantity"
                                    value={quantity}
                                    onChange={this.handleChange}
                                    maxLength={20}
                                    placeholder="Quantity"
                                />
                            </div>
                        </div>
                    )}

                    {/* Order Value - only for 3rd Party */}
                    {/* {is3rd && (
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>Order Value</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    name="order_value"
                                    value={order_value}
                                    onChange={this.handleChange}
                                    min={10000}
                                    max={1000000}
                                    placeholder="50000 - 1000000"
                                />
                            </div>
                        </div>
                    )} */}
                </div>

                <div className="row">

                    {/* Profession */}
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>Profession</label>
                            <select
                                className="form-control"
                                name="profession"
                                value={profession}
                                onChange={this.handleChange}
                            >
                                <option value="">Select</option>
                                {professionOptions.map((p) => (
                                    <option key={p.value} value={p.value}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Firm Name */}
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>Firm Name</label>
                            <input
                                type="text"
                                className="form-control"
                                name="firm_name"
                                value={firm_name}
                                onChange={this.handleChange}
                                maxLength={50}
                                placeholder="Firm Name"
                            />
                        </div>
                    </div>



                    {/* Selling Purpose */}
                    {/* <div className="col-md-3">
                        <div className="form-group">
                            <label>Selling Purpose</label>
                            <select
                                className="form-control"
                                name="selling_purpose"
                                value={selling_purpose}
                                onChange={this.handleChange}
                            >
                                <option value="">Select</option>
                                {sellingPurposeOptions.map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div> */}
                    {/* </div>

                <div className="row"> */}
                    {/* Investment */}
                    {/* <div className="col-md-3">
                        <div className="form-group">
                            <label>Investment</label>
                            <input
                                type="number"
                                className="form-control"
                                name="investment"
                                value={investment}
                                onChange={this.handleChange}
                                min={10000}
                                max={1000000}
                                placeholder="10000 - 1000000"
                            />
                        </div>
                    </div> */}


                    {/* Experience */}
                    {/* <div className="col-md-3">
                        <div className="form-group">
                            <label>Experience</label>
                            <select
                                className="form-control"
                                name="experience"
                                value={experience}
                                onChange={this.handleChange}
                            >
                                <option value="">Select</option>
                                {experienceOptions.map((ex) => (
                                    <option key={ex.value} value={ex.value}>
                                        {ex.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div> */}
                    {/* </div>

                <div className="row"> */}



                    {/* Call Time */}
                    <div className="col-md-2">
                        <div className="form-group">
                            <label>Call Time*</label>
                            <select
                                className="form-control"
                                name="call_time"
                                value={call_time}
                                onChange={this.handleChange}
                            >
                                <option value="">Select</option>
                                {callTimeOptions.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Required When */}
                    {/* <div className="col-md-3">
                        <div className="form-group">
                            <label>Required When</label>
                            <select
                                className="form-control"
                                name="required_when"
                                value={required_when}
                                onChange={this.handleChange}
                            >
                                <option value="">Select</option>
                                {requiredWhenOptions.map((r) => (
                                    <option key={r.value} value={r.value}>
                                        {r.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div> */}

                    {/* Source */}
                    <div className="col-md-2">
                        <div className="form-group">
                            <label>Source*</label>
                            {this.props.isEdit ? (
                                <input
                                    type="text"
                                    className="form-control"
                                    name="source"
                                    value={source || ""}
                                    readOnly
                                />
                            ) : (
                                <select
                                    className="form-control"
                                    name="source"
                                    value={source}
                                    onChange={this.handleChange}
                                >
                                    <option value="">Select</option>
                                    {sourceOptions.map((s) => (
                                        <option key={s.value} value={s.value}>
                                            {s.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>

                    {/* Campaign */}
                    <div className="col-md-2">
                        <div className="form-group">
                            <label>Campaign</label>
                            {this.props.isEdit ? (
                                <input
                                    type="text"
                                    className="form-control"
                                    name="campaign"
                                    value={this.state.campaign || ""}
                                    readOnly
                                />
                            ) : (
                                <select
                                    className="form-control"
                                    name="campaign"
                                    value={this.state.campaign}
                                    onChange={this.handleChange}
                                >
                                    <option value="">Select</option>
                                    {this.state.campaignOptions && this.state.campaignOptions.map((c) => (
                                        <option key={c.value} value={c.value}>
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>

                </div>

                <div className="row">
                    {/* Message */}
                    <div className="col-md-12">
                        <div className="form-group">
                            <label>Message*</label>
                            <textarea
                                className="form-control"
                                name="message"
                                value={message}
                                onChange={this.handleChange}
                                rows={4}
                                placeholder="Lead message..."
                            />
                        </div>
                    </div>
                </div>

                <div className="row">
                    {/* Verified checkbox */}
                    <div className="col-md-12">
                        <div className="form-group">
                            <label>&nbsp;</label>
                            <div className="checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        name="status"
                                        checked={status}
                                        onChange={this.handleChange}
                                    />{" "}
                                    <strong>Verified</strong>
                                </label>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Call Status & Remark */}
                <div className="row" style={{ marginTop: 10, padding: "15px 0", borderTop: "2px solid #ddd" }}>
                    <div className="col-md-12">
                        <h4 style={{ marginTop: 0, color: "#337ab7" }}>
                            <i className="fas fa-phone-alt"></i> Call Status & Remark
                        </h4>
                    </div>
                    <div className="col-md-4">
                        <div className="form-group">
                            <label>Call Status {this.props.isEdit && <span style={{ color: "red" }}>*</span>}</label>
                            <Select
                                name="call_status"
                                options={this.state.callStatusOptions}
                                value={this.state.callStatusOptions.find(opt => opt.value === this.state.call_status) || null}
                                onChange={(selected) => {
                                    const value = selected ? selected.value : "";
                                    // Trigger handleChange logic
                                    const callStatusNum = Number(value);
                                    const statusMapping = CALL_STATUS_TO_LEAD_STATUS[callStatusNum];

                                    const updates = { call_status: value };

                                    if (callStatusNum === 14) {
                                        // call_status = 14 (Done) should auto-check verified
                                        updates.status = true;
                                    } else {
                                        updates.status = false;
                                    }

                                    this.setState(updates);
                                }}
                                components={{
                                    Option: (props) => {
                                        if (!props.data) return null;
                                        const bgColor = getCallStatusOptionColor(props.data.value);
                                        return (
                                            <div
                                                {...props.innerProps}
                                                style={{
                                                    ...props.innerProps?.style,
                                                    backgroundColor: bgColor,
                                                    padding: "8px 12px",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                {props.children}
                                            </div>
                                        );
                                    },
                                    SingleValue: (props) => {
                                        if (!props.data) return null;
                                        const bgColor = getCallStatusOptionColor(props.data.value);
                                        return (
                                            <div
                                                style={{
                                                    ...props.innerProps?.style,
                                                    backgroundColor: bgColor,
                                                    padding: "4px 8px",
                                                    borderRadius: "3px",
                                                    color: "#000",
                                                }}
                                            >
                                                {props.children}
                                            </div>
                                        );
                                    },
                                }}
                                isClearable
                                placeholder="-- Select Call Status --"
                            />
                        </div>
                    </div>
                    <div className="col-md-8">
                        <div className="form-group">
                            <label>Remark</label>
                            <textarea
                                className="form-control"
                                value={this.state.remark}
                                onChange={(e) => this.setState({ remark: e.target.value })}
                                rows={2}
                                placeholder="Add your remark here..."
                            />
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <button
                            type="submit"
                            className="btn btn-success btn-lg"
                            disabled={submitting || (status && !allChecked)}
                        >
                            {submitting ? (
                                <span>
                                    <i className="fas fa-spinner fa-spin"></i> Saving...
                                </span>
                            ) : this.props.isEdit ? (
                                <span>
                                    <i className="fas fa-save"></i> Update Lead
                                </span>
                            ) : (
                                <span>
                                    <i className="fas fa-plus"></i> Add Lead
                                </span>
                            )}
                        </button>{" "}
                        <button
                            type="button"
                            className="btn btn-default btn-lg"
                            onClick={() => this.props.history.push("/leads")}
                        >
                            Cancel
                        </button>
                        {status && !allChecked && (
                            <span className="text-danger" style={{ marginLeft: 15 }}>
                                <i className="fas fa-exclamation-triangle"></i>{" "}
                                Duplicate check pending for: {pendingChecks.join(", ")}
                            </span>
                        )}
                    </div>
                </div>

                {/* Preview Modal */}
                {showPreviewModal && (
                    <div className="lead-result-modal-overlay">
                        <div className="lead-result-modal" style={{ width: '90%', maxWidth: '1000px', maxHeight: '90vh', overflowY: 'auto', textAlign: 'left', padding: 0 }}>
                            {/* Header */}
                            <div style={{ padding: '15px 20px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h4 style={{ margin: 0, color: "#337ab7" }}>Lead Preview</h4>
                                <button type="button" className="close" onClick={() => this.setState({ showPreviewModal: false })}>&times;</button>
                            </div>

                            {/* Body */}
                            <div style={{ padding: '20px' }}>
                                <div className="row">
                                    {/* Left Column */}
                                    <div className="col-md-8" style={{ borderRight: '1px solid #eee' }}>
                                        <h4 style={{ color: '#23527c', marginTop: 0, fontWeight: 'bold' }}>
                                            {service_type ? serviceTypeOptions.find((o) => o.value === service_type)?.label || service_type : "-"}
                                        </h4>

                                        <p style={{ color: '#555', fontSize: '15px', lineHeight: '1.5', marginTop: '15px' }}>
                                            {message || "No message provided."}
                                        </p>

                                        <div style={{ marginTop: '20px', fontSize: '14px' }}>
                                            <div style={{ marginBottom: '10px' }}>
                                                <span style={{ color: '#666', marginRight: '10px' }}>Medicine Type:</span>
                                                {medicine_type.length > 0 ? medicine_type.map((m, i) => (
                                                    <span key={i} className="label" style={{
                                                        marginRight: '5px',
                                                        padding: '5px 10px',
                                                        borderRadius: '4px',
                                                        backgroundColor: m.value === 'Allopathic' ? '#e6f0ff' :
                                                            m.value === 'Ayurvedic' ? '#e6ffe6' :
                                                                m.value === 'Nutraceutical' ? '#fff0e6' : '#eee',
                                                        color: m.value === 'Allopathic' ? '#0066cc' :
                                                            m.value === 'Ayurvedic' ? '#008000' :
                                                                m.value === 'Nutraceutical' ? '#cc5200' : '#333',
                                                        border: `1px solid ${m.value === 'Allopathic' ? '#b3d4ff' :
                                                            m.value === 'Ayurvedic' ? '#b3ffb3' :
                                                                m.value === 'Nutraceutical' ? '#ffc299' : '#ccc'
                                                            }`,
                                                        fontSize: '12px',
                                                        fontWeight: 'normal'
                                                    }}>
                                                        {m.label}
                                                    </span>
                                                )) : <span>-</span>}
                                            </div>

                                            <div style={{ marginBottom: '10px' }}>
                                                <span style={{ color: '#666', marginRight: '10px' }}>Primary Speciality:</span>
                                                {primary_service_for ? (
                                                    <span className="label" style={{
                                                        padding: '5px 10px',
                                                        borderRadius: '4px',
                                                        backgroundColor: '#e6ffff',
                                                        color: '#008080',
                                                        border: '1px solid #b3ffff',
                                                        fontSize: '12px',
                                                        fontWeight: 'normal'
                                                    }}>
                                                        {primary_service_for}
                                                    </span>
                                                ) : <span>-</span>}
                                            </div>

                                            <div className="row" style={{ marginTop: '20px' }}>
                                                <div className="col-md-6" style={{ marginBottom: '10px' }}>
                                                    <span style={{ color: '#666', marginRight: '5px' }}>Secondary Category:</span>
                                                    <span style={{ fontWeight: '500' }}>{service_for && service_for.length > 0 ? service_for.map(s => s.label).join(", ") : "-"}</span>
                                                </div>
                                                <div className="col-md-6" style={{ marginBottom: '10px' }}>
                                                    <span style={{ color: '#666', marginRight: '5px' }}>Call Time:</span>
                                                    <span style={{ fontWeight: '500' }}>{call_time ? callTimeOptions.find((o) => o.value === call_time)?.label || call_time : "-"}</span>
                                                </div>
                                                <div className="col-md-6" style={{ marginBottom: '10px' }}>
                                                    <span style={{ color: '#666', marginRight: '5px' }}>Source:</span>
                                                    <span style={{ fontWeight: '500' }}>{source ? sourceOptions.find((o) => o.value === source)?.label || source : "-"}</span>
                                                </div>
                                                {isPCD && (
                                                    <div className="col-md-6" style={{ marginBottom: '10px' }}>
                                                        <span style={{ color: '#666', marginRight: '5px' }}>PCD Area:</span>
                                                        <span style={{ fontWeight: '500' }}>{pcd_area ? pcd_area.label : "-"}</span>
                                                    </div>
                                                )}
                                                {is3rd && (
                                                    <div className="col-md-6" style={{ marginBottom: '10px' }}>
                                                        <span style={{ color: '#666', marginRight: '5px' }}>Quantity:</span>
                                                        <span style={{ fontWeight: '500' }}>{quantity || "-"}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column */}
                                    <div className="col-md-4">
                                        <h4 style={{ color: '#23527c', marginTop: 0, fontWeight: 'bold' }}>
                                            {name || "Unknown Name"}
                                        </h4>

                                        <div style={{ color: '#666', fontSize: '13px', marginTop: '10px' }}>
                                            <i className="fas fa-map-marker-alt" style={{ width: '20px', color: '#999', textAlign: 'center' }}></i>
                                            {address ? address.label : "Address not provided"}
                                        </div>

                                        <div style={{ marginTop: '20px', fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            <div>
                                                {mobile ? <i className="fas fa-check-circle text-success" style={{ width: '20px' }}></i> : <i className="fas fa-times-circle text-danger" style={{ width: '20px' }}></i>}
                                                <span style={{ color: '#666', marginRight: '5px' }}>Mobile:</span> <strong style={{ color: '#333' }}>{mobile || "Not Provided"}</strong>
                                            </div>
                                            <div>
                                                {whatsapp ? <i className="fab fa-whatsapp text-success" style={{ width: '20px' }}></i> : <i className="fas fa-times-circle text-danger" style={{ width: '20px' }}></i>}
                                                <span style={{ color: '#666', marginRight: '5px' }}>WhatsApp:</span> <strong style={{ color: '#333' }}>{whatsapp || "Not Provided"}</strong>
                                            </div>
                                            <div>
                                                {email ? <i className="fas fa-check-circle text-success" style={{ width: '20px' }}></i> : <i className="fas fa-times-circle text-danger" style={{ width: '20px' }}></i>}
                                                <span style={{ color: '#666', marginRight: '5px' }}>Email:</span> <strong style={{ color: '#333' }}>{email || "Not Provided"}</strong>
                                            </div>
                                            <div>
                                                {firm_name ? <i className="fas fa-check-circle text-success" style={{ width: '20px' }}></i> : <i className="fas fa-times-circle text-danger" style={{ width: '20px' }}></i>}
                                                <span style={{ color: '#666', marginRight: '5px' }}>Firm Name:</span> <strong style={{ color: '#333' }}>{firm_name || "Not Provided"}</strong>
                                            </div>
                                            <div>
                                                {profession ? <i className="fas fa-check-circle text-success" style={{ width: '20px' }}></i> : <i className="fas fa-times-circle text-danger" style={{ width: '20px' }}></i>}
                                                <span style={{ color: '#666', marginRight: '5px' }}>Profession:</span> <strong style={{ color: '#333' }}>{profession ? professionOptions.find((o) => o.value === profession)?.label || profession : "Not Provided"}</strong>
                                            </div>
                                        </div>

                                        <hr style={{ margin: '15px 0', borderColor: '#eee' }} />

                                        <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            <div>
                                                {gst_number === "1" ? <i className="fas fa-check-circle text-success" style={{ width: '20px' }}></i> : gst_number === "2" ? <i className="fas fa-clock text-warning" style={{ width: '20px' }}></i> : <i className="fas fa-times-circle text-danger" style={{ width: '20px' }}></i>}
                                                <span style={{ color: '#666' }}>{gst_number === "1" ? "GST Available" : gst_number === "2" ? "GST Applied" : "GST Not Available/Planning"}</span>
                                            </div>
                                            <div>
                                                {drug_license === "1" ? <i className="fas fa-check-circle text-success" style={{ width: '20px' }}></i> : drug_license === "2" ? <i className="fas fa-clock text-warning" style={{ width: '20px' }}></i> : <i className="fas fa-times-circle text-danger" style={{ width: '20px' }}></i>}
                                                <span style={{ color: '#666' }}>{drug_license === "1" ? "Drug License Available" : drug_license === "2" ? "Drug License Applied" : "Drug License Not Available/Planning"}</span>
                                            </div>
                                            {hasNutraceutical && (
                                                <div>
                                                    {fssai_license === "1" ? <i className="fas fa-check-circle text-success" style={{ width: '20px' }}></i> : fssai_license === "2" ? <i className="fas fa-clock text-warning" style={{ width: '20px' }}></i> : <i className="fas fa-times-circle text-danger" style={{ width: '20px' }}></i>}
                                                    <span style={{ color: '#666' }}>{fssai_license === "1" ? "FSSAI Available" : fssai_license === "2" ? "FSSAI Applied" : "FSSAI Not Available/Planning"}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div style={{ padding: '15px 20px', backgroundColor: '#f8f9fa', borderTop: '1px solid #ddd' }}>
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ fontWeight: 'normal', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                        <input
                                            type="checkbox"
                                            style={{ marginTop: '4px' }}
                                            checked={previewConfirmed}
                                            onChange={(e) => this.setState({ previewConfirmed: e.target.checked })}
                                        />
                                        <span style={{ fontSize: '14px', color: '#555' }}>
                                            <strong>I have verified these details and am ready to submit.</strong> I understand that once posted, this lead cannot be updated or deleted.
                                        </span>
                                    </label>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                    <button type="button" className="btn btn-default btn-lg" onClick={() => this.setState({ showPreviewModal: false })}>Edit Details</button>
                                    {" "}
                                    <button
                                        type="button"
                                        className="btn btn-success btn-lg"
                                        disabled={!previewConfirmed}
                                        onClick={(e) => { this.setState({ showPreviewModal: false }); this.handleSubmit(e, true); }}
                                    >
                                        Confirm & Submit
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Result Modal */}
                {resultModal && (
                    <div className="lead-result-modal-overlay">
                        <div className="lead-result-modal">
                            <div className={`lead-result-modal-icon ${resultModal.type}`}>
                                {resultModal.type === "success" ? (
                                    <i className="fas fa-check-circle"></i>
                                ) : (
                                    <i className="fas fa-times-circle"></i>
                                )}
                            </div>
                            <h3 className={resultModal.type === "success" ? "text-success" : "text-danger"}>
                                {resultModal.type === "success" ? "Success" : "Error"}
                            </h3>
                            <p>{resultModal.message.includes("\n")
                                ? resultModal.message.split("\n").map((line, i) => (
                                    <span key={i} style={{ display: "block", textAlign: "left", padding: "2px 0" }}>
                                        <i className="fas fa-exclamation-circle text-danger" style={{ marginRight: 6 }}></i>{line}
                                    </span>
                                ))
                                : resultModal.message
                            }</p>
                            {resultModal.type === "success" && resultModal.leadId && (
                                <p style={{ fontSize: 16, fontWeight: 600 }}>
                                    Lead ID: <span className="label label-info" style={{ fontSize: 15 }}>#{resultModal.leadId}</span>
                                </p>
                            )}
                            {resultModal.type === "success" && resultModal.posted !== undefined && (
                                <p style={{ fontSize: 15, fontWeight: 600, marginTop: 8 }}>
                                    Lead Status:{" "}
                                    {resultModal.posted ? (
                                        <span className="label label-success" style={{ fontSize: 14 }}>
                                            <i className="fas fa-check-circle"></i> Posted
                                        </span>
                                    ) : (
                                        <span className="label label-warning" style={{ fontSize: 14 }}>
                                            <i className="fas fa-exclamation-triangle"></i> Not Posted
                                        </span>
                                    )}
                                </p>
                            )}
                            <button
                                type="button"
                                className={`btn btn-lg ${resultModal.type === "success" ? "btn-success" : "btn-danger"}`}
                                onClick={() => {
                                    if (resultModal.type === "success") {
                                        this.props.history.push("/leads");
                                    } else {
                                        this.setState({ resultModal: null });
                                    }
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                )}
            </form>
        );
    }
}

const MessageCell = ({ text }) => {
    const [show, setShow] = React.useState(false);
    if (!text) return <span>-</span>;
    const truncated = text.length > 20 ? text.substring(0, 20) + "..." : text;
    return (
        <span
            style={{ cursor: text.length > 20 ? "pointer" : "default", maxWidth: 180, display: "inline-block" }}
            onClick={() => setShow(!show)}
            title={text}
        >
            {show ? text : truncated}
        </span>
    );
};

const DuplicateResults = ({ leads }) => {
    const [expanded, setExpanded] = React.useState(true);
    const ref = React.useRef(null);

    // Auto-collapse on outside click
    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setExpanded(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Expand when new leads arrive
    React.useEffect(() => {
        if (leads && leads.length > 0) setExpanded(true);
    }, [leads]);

    return (
        <div className="duplicate-results" ref={ref}>
            <div
                style={{ cursor: "pointer", userSelect: "none", padding: "6px 0" }}
                onClick={() => setExpanded(!expanded)}
            >
                <strong>
                    <i className={`fas fa-caret-${expanded ? "down" : "right"}`}></i>{" "}
                    <i className="fas fa-exclamation-triangle text-warning"></i>{" "}
                    {leads.length} existing lead(s) found with same {leads[0].mobile === leads[0].phone ? "phone" : "email"}
                </strong>
            </div>
            {expanded && (
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>Service Type</th>
                            <th>Medicine Type</th>
                            <th>Service For</th>
                            <th>Message</th>
                            <th>Posted On</th>
                        </tr>
                    </thead>
                    <tbody>
                        {leads.map((l, i) => {
                            const parts = [];
                            if (l.medicine_type) {
                                const mt = Array.isArray(l.medicine_type) ? l.medicine_type.join(", ") : l.medicine_type;
                                parts.push(mt);
                            }
                            const medType = parts.join(", ");
                            const serviceFor = l.service_for
                                ? (Array.isArray(l.service_for) ? l.service_for.join(", ") : l.service_for)
                                : "-";
                            return (
                                <tr key={i}>
                                    <td>{l.name}</td>
                                    <td>{l.mobile || l.phone}</td>
                                    <td>{l.service_type || "-"}</td>
                                    <td>{medType || "-"}</td>
                                    <td>{serviceFor}</td>
                                    <td><MessageCell text={l.message} /></td>
                                    <td>{l.posted_on ? new Date(l.posted_on).toLocaleDateString() : "-"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default LeadForm;
